import { addDays, format, parse } from 'date-fns';

import type { AppointmentAvailableSlot } from '@/lib/api/appointments';
import { wallClockToUtcIso } from '@/utils/appointments/appointment-timezone';
import { formatInTimezone } from '@/utils/timezone-utils';
import { isDateInTimeOff, isPastScheduleDay } from '@/utils/appointments';

export type PublicScheduleDay = {
  day: string;
  isEnabled: boolean;
  slots?: { startTime: string; endTime: string }[];
};

/** yyyy-MM-dd for a calendar grid cell (browser-local civil date). */
export function getClientCalendarDayKey(day: Date): string {
  return format(day, 'yyyy-MM-dd');
}

/** yyyy-MM-dd for an instant in the client's display timezone. */
export function getClientDisplayDayKey(instant: Date, clientTimezone: string): string {
  return formatInTimezone(instant, clientTimezone, 'yyyy-MM-dd');
}

/** Stable local Date for a calendar cell from yyyy-MM-dd (noon avoids DST edge cases). */
export function calendarDayFromYmd(ymd: string): Date {
  const parsed = parse(ymd, 'yyyy-MM-dd', new Date());
  parsed.setHours(12, 0, 0, 0);
  return parsed;
}

export function shiftScheduleDateKey(ymd: string, deltaDays: number): string {
  return format(addDays(parse(ymd, 'yyyy-MM-dd', new Date()), deltaDays), 'yyyy-MM-dd');
}

/** Weekday name in the nutritionist schedule timezone for a civil yyyy-MM-dd. */
export function getScheduleWeekdayKey(ymd: string, scheduleTimezone: string): string {
  const instant = wallClockToUtcIso(ymd, '12:00', scheduleTimezone);
  if (!instant) return '';
  return formatInTimezone(new Date(instant), scheduleTimezone, 'EEEE').toLowerCase();
}

function getClientDisplayDayKeysFromSlot(
  scheduleDateYmd: string,
  startTime: string,
  endTime: string,
  scheduleTimezone: string,
  clientTimezone: string,
): string[] {
  const keys = new Set<string>();

  for (const time of [startTime, endTime]) {
    const utc = wallClockToUtcIso(scheduleDateYmd, time, scheduleTimezone);
    if (!utc) continue;
    keys.add(getClientDisplayDayKey(new Date(utc), clientTimezone));
  }

  return [...keys];
}

function slotProjectsToClientDay(
  scheduleDateYmd: string,
  slot: { startTime: string; endTime?: string },
  scheduleTimezone: string,
  clientTimezone: string,
  clientDayKey: string,
): boolean {
  const endTime = slot.endTime || slot.startTime;
  return getClientDisplayDayKeysFromSlot(
    scheduleDateYmd,
    slot.startTime,
    endTime,
    scheduleTimezone,
    clientTimezone,
  ).includes(clientDayKey);
}

/** Nutritionist yyyy-MM-dd dates that might supply slots for a client calendar cell. */
function getScheduleDatesToProbe(day: Date, scheduleTimezone: string): string[] {
  const clientNoon = calendarDayFromYmd(getClientCalendarDayKey(day));
  const primary = formatInTimezone(clientNoon, scheduleTimezone, 'yyyy-MM-dd');
  return [...new Set([shiftScheduleDateKey(primary, -1), primary, shiftScheduleDateKey(primary, 1)])];
}

function clientDayHasProjectedSlots(
  day: Date,
  scheduleTimezone: string,
  clientTimezone: string,
  scheduleDays: PublicScheduleDay[],
): boolean {
  if (!scheduleDays.length) return true;

  const clientDayKey = getClientCalendarDayKey(day);

  for (const scheduleYmd of getScheduleDatesToProbe(day, scheduleTimezone)) {
    const weekday = getScheduleWeekdayKey(scheduleYmd, scheduleTimezone);
    const scheduleDay = scheduleDays.find((entry) => entry.day === weekday);
    if (!scheduleDay?.isEnabled) continue;

    const slots = scheduleDay.slots;
    if (slots && slots.length > 0) {
      for (const slot of slots) {
        if (slotProjectsToClientDay(scheduleYmd, slot, scheduleTimezone, clientTimezone, clientDayKey)) {
          return true;
        }
      }
      continue;
    }
  }

  // Link payload has no slot times — only the primary nutritionist weekday for this cell.
  const clientNoon = calendarDayFromYmd(clientDayKey);
  const primary = formatInTimezone(clientNoon, scheduleTimezone, 'yyyy-MM-dd');
  const weekday = getScheduleWeekdayKey(primary, scheduleTimezone);
  return scheduleDays.find((entry) => entry.day === weekday)?.isEnabled ?? false;
}

/** Nutritionist `date` query param for available-slots on a client calendar day. */
export function resolveScheduleLookupDateForSlots(
  day: Date,
  scheduleTimezone: string,
  clientTimezone: string,
  scheduleDays: PublicScheduleDay[],
  options?: { originalAppointmentStartUtc?: string },
): string {
  const dates = resolveScheduleLookupDatesForClientDay(
    day,
    scheduleTimezone,
    clientTimezone,
    scheduleDays,
    options,
  );
  return dates[0];
}

/** All nutritionist yyyy-MM-dd dates to query for slots on a client calendar day. */
export function resolveScheduleLookupDatesForClientDay(
  day: Date,
  scheduleTimezone: string,
  clientTimezone: string,
  scheduleDays: PublicScheduleDay[],
  options?: { originalAppointmentStartUtc?: string },
): string[] {
  const clientDayKey = getClientCalendarDayKey(day);
  const dates = new Set<string>();

  for (const scheduleYmd of getScheduleDatesToProbe(day, scheduleTimezone)) {
    const weekday = getScheduleWeekdayKey(scheduleYmd, scheduleTimezone);
    const scheduleDay = scheduleDays.find((entry) => entry.day === weekday);
    if (!scheduleDay?.isEnabled) continue;

    const slots = scheduleDay.slots;
    if (!slots?.length) {
      dates.add(scheduleYmd);
      continue;
    }

    if (
      slots.some((slot) =>
        slotProjectsToClientDay(scheduleYmd, slot, scheduleTimezone, clientTimezone, clientDayKey),
      )
    ) {
      dates.add(scheduleYmd);
    }
  }

  if (options?.originalAppointmentStartUtc) {
    const originalStart = new Date(options.originalAppointmentStartUtc);
    if (
      !Number.isNaN(originalStart.getTime()) &&
      isSameClientCalendarDay(day, originalStart, clientTimezone)
    ) {
      dates.add(formatInTimezone(originalStart, scheduleTimezone, 'yyyy-MM-dd'));
    }
  }

  if (dates.size === 0 && scheduleDays.length > 0) {
    for (const scheduleYmd of getScheduleDatesToProbe(day, scheduleTimezone)) {
      const weekday = getScheduleWeekdayKey(scheduleYmd, scheduleTimezone);
      if (scheduleDays.find((entry) => entry.day === weekday)?.isEnabled) {
        dates.add(scheduleYmd);
      }
    }
  }

  if (dates.size === 0) {
    const clientNoon = calendarDayFromYmd(clientDayKey);
    dates.add(formatInTimezone(clientNoon, scheduleTimezone, 'yyyy-MM-dd'));
  }

  return [...dates].sort();
}

export function mergeAvailableSlotsByUtc(slots: AppointmentAvailableSlot[]): AppointmentAvailableSlot[] {
  const seen = new Set<string>();
  const merged: AppointmentAvailableSlot[] = [];

  for (const slot of slots) {
    const key = slot.startTimeUtc || `${slot.startTime}-${slot.endTime}`;
    if (seen.has(key)) continue;
    seen.add(key);
    merged.push(slot);
  }

  return merged.sort((a, b) => a.startTime.localeCompare(b.startTime));
}

/** Keep only slots whose start instant falls on the selected client calendar day. */
export function filterSlotsForClientCalendarDay(
  slots: AppointmentAvailableSlot[],
  day: Date,
  clientTimezone: string,
): AppointmentAvailableSlot[] {
  const clientDayKey = getClientCalendarDayKey(day);

  return slots.filter((slot) => {
    if (!slot.startTimeUtc) return true;
    const instant = new Date(slot.startTimeUtc);
    if (Number.isNaN(instant.getTime())) return true;
    return getClientDisplayDayKey(instant, clientTimezone) === clientDayKey;
  });
}

export function isSameClientCalendarDay(
  day: Date,
  instant: Date,
  clientTimezone: string,
): boolean {
  return getClientCalendarDayKey(day) === getClientDisplayDayKey(instant, clientTimezone);
}

/**
 * Public booking / reschedule calendar — a day is selectable only when schedule slot
 * times actually project onto that client calendar day (plus the current appointment day
 * on reschedule).
 */
export function isPublicBookingCalendarDaySelectable(
  day: Date,
  options: {
    timeOff: Array<{ startDate: string; endDate?: string }>;
    clientTimezone: string;
    scheduleTimezone: string;
    scheduleDays?: PublicScheduleDay[];
    /** Keep the current appointment day selectable on reschedule. */
    originalAppointmentStartUtc?: string;
  },
): boolean {
  if (isPastScheduleDay(day)) return false;
  if (isDateInTimeOff(day, options.timeOff)) return false;

  if (options.originalAppointmentStartUtc) {
    const originalStart = new Date(options.originalAppointmentStartUtc);
    if (
      !Number.isNaN(originalStart.getTime()) &&
      isSameClientCalendarDay(day, originalStart, options.clientTimezone)
    ) {
      return true;
    }
  }

  return clientDayHasProjectedSlots(
    day,
    options.scheduleTimezone,
    options.clientTimezone,
    options.scheduleDays ?? [],
  );
}
